import { useLanguage } from '../context/LanguageContext';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Camera, Cpu, Zap, Eye, Wifi, Battery } from 'lucide-react';
import useSEOPage from '../hooks/useSEOPage';

export default function Technologie() {
  const { t } = useLanguage();
  const { seoData } = useSEOPage('technologie');

  return (
    <>
      {seoData && (
        <Helmet>
          <title>{seoData.meta_title || seoData.title || 'Technologie Joey Cyan | Innovation AR & IA'}</title>
          <meta name="description" content={seoData.meta_description || seoData.description || 'Découvrez les technologies révolutionnaires intégrées dans nos lunettes connectées : réalité augmentée, intelligence artificielle, caméra 4K.'} />
          {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}
          {seoData.og_image && <meta property="og:image" content={seoData.og_image} />}
        </Helmet>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-16">
          {/* Hero */}
          <div className="text-center mb-16">
            <p className="text-xs text-cyan-400 uppercase tracking-widest font-semibold mb-2">Innovation</p>
            <h1 className="text-4xl md:text-6xl font-black mb-4 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              {t('technology.title')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Des années de R&D condensées dans 18 grammes d'innovation
            </p>
          </div>

          {/* Tech Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-cyan-400 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                <Camera size={28} />
              </div>
              <h3 className="text-xl font-bold mb-2">Caméra 12MP</h3>
              <p className="text-gray-300 text-sm mb-3">
                Capteur Sony IMX586 avec stabilisation optique OIS. Photos Ultra HD et vidéos 4K à 30fps.
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• FOV 82° ultra-wide</li>
                <li>• Mode rafale 10 photos/sec</li>
                <li>• Night mode AI</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-cyan-400 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                <Cpu size={28} />
              </div>
              <h3 className="text-xl font-bold mb-2">IA Embarquée</h3>
              <p className="text-gray-300 text-sm mb-3">
                Puce Qualcomm Snapdragon XR2+ Gen 2 avec NPU dédié. GPT-5.2 Vision intégré.
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Traduction instantanée (95 langues)</li>
                <li>• Reconnaissance d'objets</li>
                <li>• Assistant vocal contextuel</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-cyan-400 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <Eye size={28} />
              </div>
              <h3 className="text-xl font-bold mb-2">Réalité Augmentée</h3>
              <p className="text-gray-300 text-sm mb-3">
                Affichage holographique Micro-LED. Navigation GPS en overlay dans votre champ de vision.
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Résolution 2K par œil</li>
                <li>• Luminosité 2000 nits</li>
                <li>• SLAM 6DOF tracking</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-cyan-400 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                <Zap size={28} />
              </div>
              <h3 className="text-xl font-bold mb-2">Audio Spatial</h3>
              <p className="text-gray-300 text-sm mb-3">
                Haut-parleurs directionnels + conduction osseuse. Son immersif 360° compatible Dolby Atmos.
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• 6 micros anti-bruit (35dB)</li>
                <li>• Appels HD Voice</li>
                <li>• Basses renforcées</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-cyan-400 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                <Wifi size={28} />
              </div>
              <h3 className="text-xl font-bold mb-2">Connectivité</h3>
              <p className="text-gray-300 text-sm mb-3">
                Bluetooth 5.3 LE, WiFi 6E tri-band. Synchronisation ultra-rapide avec tous vos appareils.
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Portée 100m Bluetooth</li>
                <li>• Multi-device (3 appareils)</li>
                <li>• NFC pairing</li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-cyan-400 transition-colors">
              <div className="w-14 h-14 bg-gradient-to-br from-red-400 to-pink-500 rounded-xl flex items-center justify-center mb-4">
                <Battery size={28} />
              </div>
              <h3 className="text-xl font-bold mb-2">Autonomie</h3>
              <p className="text-gray-300 text-sm mb-3">
                Batterie Lithium-Polymère 500mAh. Charge rapide USB-C ou sans fil (Qi compatible).
              </p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• 12h audio continu</li>
                <li>• 4h vidéo 4K</li>
                <li>• 10 min = 3h d'usage</li>
              </ul>
            </div>
          </div>

          {/* Specs Table */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/10 mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Spécifications techniques</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-cyan-400 mb-3">Hardware</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex justify-between"><span>Processeur</span><span className="text-white">Snapdragon XR2+ Gen 2</span></li>
                  <li className="flex justify-between"><span>RAM</span><span className="text-white">8 GB LPDDR5</span></li>
                  <li className="flex justify-between"><span>Stockage</span><span className="text-white">128 GB UFS 3.1</span></li>
                  <li className="flex justify-between"><span>Poids</span><span className="text-white">18 grammes</span></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-cyan-400 mb-3">Capteurs</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex justify-between"><span>Gyroscope</span><span className="text-white">9 axes (IMU)</span></li>
                  <li className="flex justify-between"><span>GPS</span><span className="text-white">Dual-band + GLONASS</span></li>
                  <li className="flex justify-between"><span>Luminosité</span><span className="text-white">Auto-adapt (0-100k lux)</span></li>
                  <li className="flex justify-between"><span>Étanchéité</span><span className="text-white">IP67</span></li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-4">Découvrez la puissance de Joey Cyan</h2>
            <p className="text-xl mb-6 text-blue-50">Technologie de pointe à prix accessible</p>
            <a 
              href="/collection" 
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-xl"
            >
              Voir la collection →
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
